import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMaintenance = () => {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    fetchMaintenanceStatus();
  }, [navigate]);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/maintenance`);
      setEnabled(response.data.enabled || false);
      setMessage(response.data.message || '');
    } catch (error) {
      console.error('Error carregant configuració:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', !enabled);
      formData.append('message', message || 'Estem fent millores a la botiga. Tornem aviat!');
      
      await axios.put(`${API}/settings/maintenance`, formData);
      setEnabled(!enabled);
      
      if (!enabled) {
        toast.success('🔧 Mode manteniment ACTIVAT. La botiga no és accessible pels clients.');
      } else {
        toast.success('✅ Mode manteniment DESACTIVAT. La botiga ja és accessible.');
      }
    } catch (error) {
      console.error('Error actualitzant configuració:', error);
      toast.error('Error actualitzant la configuració');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMessage = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', enabled);
      formData.append('message', message);
      
      await axios.put(`${API}/settings/maintenance`, formData);
      toast.success('Missatge guardat correctament');
    } catch (error) {
      console.error('Error guardant missatge:', error);
      toast.error('Error guardant el missatge');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar al panell
          </Link>
          <h1 className="font-heading text-4xl font-medium text-foreground">
            Mode Manteniment
          </h1>
          <p className="text-muted-foreground mt-2">
            Activa el mode manteniment per impedir l'accés a la botiga mentre fas canvis.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Status Card */}
          <div className={`rounded-2xl p-8 ${enabled ? 'bg-orange-100 border-2 border-orange-400' : 'bg-green-100 border-2 border-green-400'}`}>
            <div className="flex items-center gap-4 mb-4">
              {enabled ? (
                <AlertTriangle className="h-10 w-10 text-orange-600" />
              ) : (
                <Wrench className="h-10 w-10 text-green-600" />
              )}
              <div>
                <h2 className="text-2xl font-semibold">
                  {enabled ? 'Botiga en Manteniment' : 'Botiga Operativa'}
                </h2>
                <p className={`text-sm ${enabled ? 'text-orange-700' : 'text-green-700'}`}>
                  {enabled 
                    ? 'Els clients no poden accedir a la botiga' 
                    : 'Els clients poden comprar amb normalitat'}
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleToggle}
              disabled={saving}
              className={`w-full h-14 text-lg font-semibold ${enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : null}
              {enabled ? '✅ Desactivar Manteniment' : '🔧 Activar Manteniment'}
            </Button>
          </div>

          {/* Message Card */}
          <div className="bg-card rounded-2xl p-6 shadow-sm">
            <h3 className="font-semibold text-lg mb-4">Missatge pels visitants</h3>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Estem fent millores a la botiga. Tornem aviat!"
              className="w-full h-32 p-4 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button
              onClick={handleSaveMessage}
              disabled={saving}
              variant="outline"
              className="mt-4"
            >
              Guardar missatge
            </Button>
          </div>

          {/* Info Card */}
          <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">ℹ️ Informació</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• El panell d'administració sempre serà accessible</li>
              <li>• Els clients veuran una pàgina de "En obres"</li>
              <li>• Les comandes existents no es veuran afectades</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminMaintenance;
